import type { ReactNode } from 'react';
import { Box, Button, Heading, InlineStack, Text, Tooltip, TooltipContent } from '@customerio/pluma-components/react';
import { ImportSummary } from '@/features/sql-sync';
import { isEmptyString } from '@/utils/string';
import { useSqlImportDetail } from '../context';
import { summaryInput } from '../summary-input';
import { useImportActions } from '../use-import-actions';
import { InlineEditText } from './inline-edit-text';
import { MANAGE_PERMISSION_TOOLTIP } from './import-configuration';

type GatedButtonProps = {
  isAllowed: boolean;
  children: ReactNode;
};

/** A header action, with Ember's permission tooltip when the viewer cannot use it. */
function GatedButton({ isAllowed, children }: GatedButtonProps) {
  if (isAllowed) {
    return <>{children}</>;
  }
  return (
    <Tooltip isCustom={true}>
      {(context) => (
        <>
          <Box ref={context.reference.setReference} {...context.reference.getReferenceProps()}>
            {children}
          </Box>
          <TooltipContent>{MANAGE_PERMISSION_TOOLTIP}</TooltipContent>
        </>
      )}
    </Tooltip>
  );
}

/** The import's name and description, edited in place, and the Delete and Pause/Activate actions. */
export function ImportHeader() {
  const { workspaceId, sqlImport, syncTarget, segment, canEdit, canDelete } = useSqlImportDetail();
  const actions = useImportActions({
    summary: (
      <ImportSummary
        workspaceId={workspaceId}
        sqlImport={summaryInput(sqlImport)}
        syncTarget={syncTarget}
        segment={segment}
        isProgressive
      />
    ),
  });

  return (
    <InlineStack alignBlock="start" alignInline="space-between" gap="300" mb="200">
      <Box style={{ flex: 1, minWidth: 0 }}>
        <InlineEditText
          value={sqlImport.name}
          label="name"
          isDisabled={!canEdit}
          onSave={(name) => actions.updateDetails({ name })}
        >
          <Heading level="2" color="bold" data-test-sql-import-name>
            {sqlImport.name}
          </Heading>
        </InlineEditText>
        <InlineEditText
          value={sqlImport.description}
          label="description"
          placeholder="Add a description"
          isMultiline
          allowsEmpty
          isDisabled={!canEdit}
          onSave={(description) => actions.updateDetails({ description })}
        >
          <Text color="subtle" data-test-sql-import-description>
            {isEmptyString(sqlImport.description) ? 'No description' : sqlImport.description}
          </Text>
        </InlineEditText>
      </Box>
      <InlineStack gap="100" alignBlock="center">
        <GatedButton isAllowed={canDelete}>
          <Button
            variant="secondary"
            isCritical
            isDisabled={!canDelete}
            onClick={actions.openDeleteModal}
            data-test-sql-import-button="delete"
          >
            Delete sync
          </Button>
        </GatedButton>
        <GatedButton isAllowed={canEdit}>
          <Button
            variant={sqlImport.active ? 'secondary' : 'primary'}
            isDisabled={!canEdit}
            onClick={actions.openToggleStatusModal}
            data-test-sql-import-button="toggle-status"
          >
            {sqlImport.active ? 'Pause sync' : 'Activate sync'}
          </Button>
        </GatedButton>
      </InlineStack>
    </InlineStack>
  );
}
